import type {
	CanvasItemFrontend,
	CanvasSectionConfigs,
	CanvasSectionsFrontend,
	ParsedCanvasFrontend,
	TFlowHeading,
	TFlowItemFrontend,
	TMention
} from '@/types/common';
import { getCompactCardHeight } from '@/utils/get-flow-card-height.ts';

class FlowParser {
	private sections: CanvasSectionsFrontend;
	private configs: CanvasSectionConfigs;

	private incomingMentionsHash: Record<string, TMention[]> = {};

	public itemsDictionary: Record<string, CanvasItemFrontend> = {};
	public flowHeadings: TFlowHeading[] = [];
	public flowItems: TFlowItemFrontend[] = [];
	public groupedItems: Record<string, TFlowItemFrontend[]> = {};

	constructor(data: ParsedCanvasFrontend) {
		this.sections = data.sections;
		this.configs = data.configs;

		if (data.sections && data.configs) {
			this.build();
		}
	}

	private build() {
		Object.entries(this.sections).forEach(
			([sectionId, items], sectionIndex) => {
				this.flowHeadings.push({
					id: `type-${sectionId}`,
					source: this.configs[sectionId]
				});

				const builtItems = items.map((item, idx) =>
					this.buildFlowItem(sectionId, item, sectionIndex, idx)
				);

				this.flowItems.push(...builtItems);
				this.groupedItems[sectionId] = builtItems;
			}
		);
	}

	private buildFlowItem(
		sectionId: string,
		item: CanvasItemFrontend,
		sectionIndex = 0,
		itemIndex = 0
	): TFlowItemFrontend {
		this.itemsDictionary[item.idn] = item;

		const incomingMentions: TMention[] =
			this.incomingMentionsHash[item.idn] ?? [];
		const outgoingMentions: TMention[] = [];
		const brokenMentions: TMention[] = [];

		for (const mention of item.mentions ?? []) {
			if (this.itemsDictionary[mention.item_idn]) {
				outgoingMentions.push(mention);
			} else {
				brokenMentions.push(mention);
			}

			if (!this.incomingMentionsHash[mention.item_idn]) {
				this.incomingMentionsHash[mention.item_idn] = [];
			}
			this.incomingMentionsHash[mention.item_idn].push({
				item_idn: item.idn,
				type: sectionId
			});
		}

		return {
			source: item,
			cardHeight: getCompactCardHeight(),
			type: sectionId,
			isCollapsed: false,
			color: this.configs[sectionId].color,
			incomingMentions,
			brokenMentions,
			outgoingMentions,
			cellX: itemIndex,
			cellY: sectionIndex
		};
	}

	public addItem(
		sectionId: string,
		item: CanvasItemFrontend,
		sectionIndex = 0,
		itemIndex = 0
	): TFlowItemFrontend {
		const flowItem = this.buildFlowItem(
			sectionId,
			item,
			sectionIndex,
			itemIndex
		);

		this.flowItems.push(flowItem);

		if (!this.groupedItems[sectionId]) {
			this.groupedItems[sectionId] = [];
		}
		this.groupedItems[sectionId].push(flowItem);

		return flowItem;
	}
}

export default FlowParser;
